import React from 'react';
import { useNavigate } from 'react-router-dom';
import { RoofBeamWordmark } from '../components/RoofBeamLogo';

// Public marketing landing at / for logged-out visitors. Static and
// dependency-free on purpose — it ships in the main bundle and paints
// instantly. Reuses the app's CSS vars and .btn classes.

const FEATURES = [
  ['⚡', 'Storm Intelligence', 'Live radar, hail and wind history, and instant alerts when storms cross your leads.'],
  ['🎯', 'Lead Engine', 'Thousands of scored, skip-traced homeowner leads generated automatically.'],
  ['🚪', 'Canvass Maps', 'Satellite door-knocking maps with owner info and one-tap outcomes.'],
  ['✍️', 'Close at the Door', 'Proposals, contingencies, and contracts e-signed on the spot.'],
  ['📐', 'Takeoffs & Estimates', 'Import a roof report, get material lists and priced estimates in seconds.'],
  ['📊', 'Run the Business', 'Pipeline, crews, photos, invoices, and profit in one place.'],
];

// Map dots: [top%, left%, color, pulse?]
const DOTS = [
  [22, 18, '#e74c3c', true], [38, 34, '#e74c3c', true], [61, 26, '#e74c3c', true],
  [30, 62, '#f39c12', false], [55, 58, '#f39c12', false],
  [70, 74, '#9b59b6', false], [24, 80, '#9b59b6', false],
  [76, 42, '#1abc9c', false], [46, 84, '#1abc9c', false],
];

export default function Landing() {
  const navigate = useNavigate();
  const scrollToFeatures = () => document.getElementById('rb-features')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="rb-landing">
      {/* Hero */}
      <section className="rb-hero">
        <div className="rb-logotype"><RoofBeamWordmark size={40} /></div>
        <h1 className="rb-headline">From storm to signature.</h1>
        <p className="rb-subline">
          The roofing CRM built by a roofer — storm intelligence, lead generation,
          doorstep closing, and e-signed contracts in one system.
        </p>
        <div className="rb-cta-row">
          <button className="btn btn-primary rb-cta" onClick={() => navigate('/login')}>Sign In</button>
          <button className="btn btn-ghost rb-cta" onClick={scrollToFeatures}>See what's inside</button>
        </div>

        {/* Hero visual — stylized storm/lead map, pure CSS */}
        <div className="rb-map-wrap">
          <div className="rb-map">
            {DOTS.map(([top, left, color, pulse], i) => (
              <span key={i} className={'rb-dot' + (pulse ? ' rb-dot-pulse' : '')}
                style={{ top: `${top}%`, left: `${left}%`, background: color, boxShadow: `0 0 10px 2px ${color}66` }} />
            ))}
          </div>
          <div className="rb-chip rb-chip-tl">
            <div className="rb-chip-num" style={{ color: '#e74c3c' }}>🔥 649</div>
            <div className="rb-chip-label">Hot Leads</div>
          </div>
          <div className="rb-chip rb-chip-br">
            <div className="rb-chip-num" style={{ color: '#f39c12' }}>⚡ Storm Alert</div>
            <div className="rb-chip-label">340 leads in path</div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="rb-features" className="rb-features">
        {FEATURES.map(([icon, title, line]) => (
          <div key={title} className="card rb-feature">
            <div className="rb-feature-icon">{icon}</div>
            <div className="rb-feature-title">{title}</div>
            <div className="rb-feature-line">{line}</div>
          </div>
        ))}
      </section>

      {/* Closing band */}
      <section className="rb-closing">
        <p className="rb-closing-line">
          Built in the field by KLO Roofing. Battle-tested on real jobs, real storms, real doors.
        </p>
        <button className="btn btn-primary rb-cta" onClick={() => navigate('/login')}>Sign In</button>
      </section>

      <footer className="rb-footer">
        RoofBeam™ · a KLO Roofing company · FL CCC1336568
      </footer>

      <style>{`
        .rb-landing { min-height: 100vh; background: #111; color: var(--text); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; overflow-x: hidden; }
        .rb-hero { max-width: 760px; margin: 0 auto; padding: clamp(48px, 10vh, 96px) var(--sp-5) var(--sp-6); text-align: center; }
        .rb-logotype { font-size: clamp(22px, 4vw, 28px); font-weight: 900; color: var(--red); letter-spacing: -0.5px; margin-bottom: clamp(28px, 6vh, 52px); }
        .rb-headline { font-size: clamp(34px, 7vw, 56px); font-weight: 800; line-height: 1.08; letter-spacing: -1px; margin-bottom: var(--sp-4); }
        .rb-subline { font-size: clamp(15px, 2.4vw, 18px); line-height: 1.65; color: var(--text-muted); max-width: 560px; margin: 0 auto var(--sp-5); }
        .rb-cta-row { display: flex; gap: var(--sp-3); justify-content: center; flex-wrap: wrap; margin-bottom: clamp(36px, 7vh, 64px); }
        .rb-cta { padding: 14px 28px; font-size: var(--fs-md); min-height: 48px; }

        .rb-map-wrap { position: relative; max-width: 620px; margin: 0 auto; padding: 0 var(--sp-3); }
        .rb-map {
          position: relative; height: clamp(220px, 38vw, 300px);
          background:
            repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(255,255,255,0.045) 40px),
            repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(255,255,255,0.045) 40px),
            var(--dark2);
          border: 1px solid var(--border); border-radius: var(--radius-lg);
          box-shadow: var(--shadow-card); overflow: hidden;
        }
        .rb-dot { position: absolute; width: 9px; height: 9px; border-radius: 50%; }
        .rb-dot-pulse { animation: rbPulse 2.6s ease-in-out infinite; }
        @keyframes rbPulse { 0%, 100% { opacity: 0.65; transform: scale(1); } 50% { opacity: 1; transform: scale(1.35); } }

        .rb-chip {
          position: absolute; background: var(--dark2); border: 1px solid var(--border);
          border-radius: var(--radius-md); box-shadow: var(--shadow-raised);
          padding: 10px 14px; text-align: left;
        }
        .rb-chip-tl { top: -18px; left: 0; border-top: 3px solid #e74c3c; }
        .rb-chip-br { bottom: -18px; right: 0; border-top: 3px solid #f39c12; }
        .rb-chip-num { font-size: 17px; font-weight: 800; line-height: 1.2; }
        .rb-chip-label { font-size: var(--fs-xs); color: var(--text-muted); margin-top: 2px; }

        .rb-features {
          max-width: 860px; margin: 0 auto; padding: clamp(40px, 8vh, 72px) var(--sp-5);
          display: grid; grid-template-columns: 1fr; gap: var(--sp-4);
        }
        @media (min-width: 720px) { .rb-features { grid-template-columns: 1fr 1fr; } }
        .rb-feature { text-align: left; }
        .rb-feature-icon { font-size: 26px; margin-bottom: var(--sp-2); }
        .rb-feature-title { font-size: var(--fs-md); font-weight: 700; margin-bottom: 6px; }
        .rb-feature-line { font-size: var(--fs-sm); line-height: 1.6; color: var(--text-muted); }

        .rb-closing { text-align: center; padding: clamp(40px, 8vh, 72px) var(--sp-5); border-top: 1px solid var(--border); }
        .rb-closing-line { font-size: clamp(16px, 2.6vw, 20px); font-weight: 600; line-height: 1.6; max-width: 620px; margin: 0 auto var(--sp-5); }

        .rb-footer { text-align: center; padding: var(--sp-5); font-size: var(--fs-xs); color: var(--text-muted); border-top: 1px solid var(--border); }
      `}</style>
    </div>
  );
}
